import { FC, useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import type { TemplateByIdResponse } from '../../../hooks/use-template-by-id.hook';

import { CheckIcon, CrossIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { ModalWrapper } from '../../../../../../components/modal-wrapper/index';
import { useFormat, useLocale } from '../../../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../../../storage/company/index';
import { eventsModalStore } from '../../../../../events/store/events-modal.store';
import { useTemplateDocument } from '../../../hooks/use-template-document.hook';
import { useTemplatePreview } from '../../../hooks/use-template-preview.hook';
import { MappingEditorStore } from '../../../store/mapping-editor.store';
import { getFieldMappingDisplayLabel } from '../../../utils/field-mapping-display-label.util';
import { DocumentPreview } from './document/document-preview';

type GrantItemFormValueResolver = (
  item: GrantItemForReview,
  formatDate: (date: Date, formatStr: string) => string,
) => string;

const GRANT_ITEM_FIELD_RESOLVERS: Record<string, GrantItemFormValueResolver> = {
  'grant-date': (item, formatDate) => formatDate(item.grantDate, 'dd/MM/yyyy'),
  'grant-amount': (item) => String(item.shares.toLocaleString('en-US')),
};

export type GranteeForReview = {
  email?: string;
  fullName: string;
  grantsCount?: number;
  id: string;
};

export type GrantItemForReview = {
  grantDate: Date;
  sharePlan?: { id: string };
  shares: number;
  stakeholder: { id: string };
};

export type ReviewDocumentsModalProps = {
  grantees: GranteeForReview[];
  grantItems: GrantItemForReview[];
  template: null | TemplateByIdResponse;
};

const MappedValueCheck: FC = () => (
  <span
    aria-hidden
    className="flex size-4 shrink-0 items-center justify-center rounded-full bg-forest-500"
  >
    <CheckIcon className="size-2.5" iconColor="#fff" />
  </span>
);

export const ReviewDocumentsModal: FC<ReviewDocumentsModalProps> = ({
  grantees,
  grantItems,
  template,
}) => {
  const handleDismiss = eventsModalStore(({ handleDismiss }) => handleDismiss);
  const open = eventsModalStore(({ open }) => open);

  const [selectedGrantItemIndex, setSelectedGrantItemIndex] = useState(0);
  const { format } = useFormat();
  const { messagesLocale, messagesLocaleFn } = useLocale();
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const setVariableMappings = MappingEditorStore(({ setVariableMappings }) => setVariableMappings);

  const selectedGrantItem = useMemo(() => {
    const idx = selectedGrantItemIndex;
    if (idx < 0 || idx >= grantItems.length) return null;
    return grantItems[idx] ?? null;
  }, [grantItems, selectedGrantItemIndex]);

  const previewParams = useMemo(
    () => ({
      companyId: companyId ?? null,
      planId: selectedGrantItem?.sharePlan?.id ?? null,
      stakeholderId: selectedGrantItem?.stakeholder.id ?? null,
    }),
    [companyId, selectedGrantItem?.sharePlan?.id, selectedGrantItem?.stakeholder.id],
  );

  const { fillValues: previewFillValues } = useTemplatePreview(
    template?.id ?? null,
    previewParams,
    open && !!template?.id && !!selectedGrantItem,
  );

  const mergedFillValues = useMemo(() => {
    const fieldMapping = template?.fieldMapping ?? {};
    const merged = { ...previewFillValues };
    if (!selectedGrantItem) return merged;
    for (const [placeholder, field] of Object.entries(fieldMapping)) {
      const resolveFromForm = GRANT_ITEM_FIELD_RESOLVERS[field];
      if (resolveFromForm) {
        merged[placeholder] = resolveFromForm(selectedGrantItem, format);
      }
    }
    return merged;
  }, [template?.fieldMapping, previewFillValues, selectedGrantItem, format]);

  const { file } = useTemplateDocument({
    templateId: template?.id ?? null,
    enabled: open && !!template?.id,
    fileLink: template?.fileLink,
  });

  const fieldMappings = template?.fieldMapping
    ? Object.entries(template.fieldMapping).map(([placeholder, field]) => ({ field, placeholder }))
    : [];

  useEffect(() => {
    if (!open) return;
    setVariableMappings(mergedFillValues);
  }, [open, mergedFillValues, setVariableMappings]);

  useEffect(() => {
    if (grantItems.length === 0) {
      setSelectedGrantItemIndex(0);
      return;
    }
    setSelectedGrantItemIndex((prev) => (prev >= grantItems.length ? 0 : prev));
  }, [grantItems.length]);

  return (
    <ModalWrapper
      className={twMerge(
        '!bottom-[25px] !left-[66px] !right-[66px] !top-[25px] !z-[61] flex !h-auto !w-auto !max-w-none !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
      )}
      control={{
        open,
        onOpenChange: (open: boolean) => {
          if (!open) handleDismiss();
        },
      }}
      onBgClick={handleDismiss}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className={twMerge('flex min-h-0 flex-1 flex-col', 'overflow-hidden')}>
        <div className="flex shrink-0 flex-col gap-1 px-6 pt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] font-semibold text-gray-700">
              <AppFormattedMessage id="REVIEW_DOCUMENTS" />
            </h2>
            <Button
              className="h-8 w-8 shrink-0 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
              onClick={handleDismiss}
              styleType="NONE"
              type="button"
            >
              <CrossIcon className="h-4 w-4" iconColor="#344054" />
            </Button>
          </div>
          <p className="text-sm text-gray-400">
            <AppFormattedMessage id="REVIEW_DOCUMENTS_SUBTITLE" />
          </p>
        </div>

        <div className="flex min-h-0 flex-1 gap-6 overflow-hidden px-6 py-6">
          <aside
            className="flex w-72 shrink-0 flex-col overflow-hidden rounded-2xl bg-white"
            style={{
              boxShadow:
                '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
            }}
          >
            <div className="shrink-0 px-3 pt-3">
              <span className="text-xs text-gray-500">
                <AppFormattedMessage id="GRANTEES" />
              </span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              {grantees.map((grantee, index) => {
                const isSelected = index === selectedGrantItemIndex;
                return (
                  <button
                    className={twMerge(
                      'relative w-full rounded-lg border p-3 text-left transition-colors',
                      isSelected ? 'border-forest-200 bg-forest-25' : 'border-gray-200 bg-gray-25',
                    )}
                    key={`grant-item-${index}`}
                    onClick={() => setSelectedGrantItemIndex(index)}
                    type="button"
                  >
                    <span
                      aria-hidden
                      className="absolute right-3 top-3 flex size-4 shrink-0 items-center justify-center rounded-full bg-forest-500"
                    >
                      <CheckIcon className="size-2.5" iconColor="#fff" />
                    </span>
                    <div className="min-w-0 space-y-1 pr-6">
                      <p className="truncate text-sm font-medium text-gray-700">
                        {grantee.fullName}
                      </p>
                      {grantee.email != null && grantee.email !== '' && (
                        <p className="truncate text-xs text-gray-500">{grantee.email}</p>
                      )}
                      {grantee.grantsCount != null && grantee.grantsCount > 0 && (
                        <p className="text-xs text-gray-500">
                          {grantee.grantsCount.toLocaleString('en-US')}{' '}
                          <AppFormattedMessage id="GRANTS" />
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <DocumentPreview
            allFieldsAsMapped
            file={file ?? undefined}
            fileName={template?.documentName ?? undefined}
          />

          <aside
            className="flex w-80 shrink-0 flex-col overflow-hidden rounded-2xl bg-white"
            style={{
              boxShadow:
                '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
            }}
          >
            <div className="flex shrink-0 items-center justify-between px-3 pt-3">
              <span className="text-xs text-gray-500">
                <AppFormattedMessage id="VARIABLE_MAPPINGS" />
              </span>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-3">
              {fieldMappings.map((mapping) => (
                <div
                  className="flex min-h-[68px] items-stretch gap-3 rounded-lg border border-gray-100 p-3"
                  key={mapping.placeholder}
                >
                  <div className="flex min-w-0 flex-1 flex-col justify-between">
                    <div className="break-all text-xs text-gray-700">{mapping.placeholder}</div>
                    <div className="truncate text-xs text-forest-600">
                      {mergedFillValues[mapping.placeholder] !== undefined &&
                      mergedFillValues[mapping.placeholder] !== ''
                        ? mergedFillValues[mapping.placeholder]
                        : getFieldMappingDisplayLabel({
                            messagesLocale,
                            messagesLocaleFn,
                            storedValue: mapping.field,
                          })}
                    </div>
                  </div>
                  <MappedValueCheck />
                </div>
              ))}
            </div>
          </aside>
        </div>

        <div className="flex shrink-0 justify-end bg-gray-100 px-6 py-4">
          <Button
            className="bg-brand-700 px-4 py-2 text-sm font-medium text-white"
            onClick={handleDismiss}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CLOSE" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
